import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { MAX_LENGTH_PROJECT_NAME, Project } from "shared/types/Project"
import { TextInput } from "shared/ui/input/text/TextInput"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { useDuplicateProject } from "entities/projects/api/useDuplicateProject"

import { useDuplicateProjectOverlay } from "./hooks/useDuplicateProjectOverlay"

const FORM_ID = "duplicate-project-form"

export interface DuplicateProjectOverlayViewModel {
  project: Project | null | undefined
  autoFocus?: boolean
  onDuplicate: ReturnType<typeof useDuplicateProject>["duplicateProject"]
  onClose: () => void
}

export const DuplicateProjectOverlayView = React.memo(
  (props: DuplicateProjectOverlayViewModel) => {
    const { t } = useTranslation()
    const { form, autoFocus, isSubmitting, onSubmit, onCancel } =
      useDuplicateProjectOverlay(props)

    return (
      <FormOverlay
        id="duplicate-project-overlay"
        form={FORM_ID}
        prefix="duplicate-project-overlay"
        className="form-overlay--small"
        headline={t("Project.Actions.Duplicate")}
        onCancel={onCancel}
        isLoading={isSubmitting}
        isSubmitDisabled={isSubmitting}
        showCloseButton
      >
        <FormProvider {...form}>
          <form id={FORM_ID} className="overlay-form" onSubmit={onSubmit}>
            <TextInput.Root
              name="title"
              data-testid="duplicate-project-overlay__title"
              label={t("Project.title")}
              autoFocus={autoFocus}
              maxLength={MAX_LENGTH_PROJECT_NAME}
              required
            >
              <TextInput.Label />
              <TextInput.Focus>
                <TextInput.Input
                  type="text"
                  data-testid="duplicate-project-overlay__title__input"
                />
                <TextInput.Counter />
              </TextInput.Focus>
              <TextInput.Readonly />
              <TextInput.ErrorMessage />
            </TextInput.Root>
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
